/**
 * FlyhubPage.jsx — EcosystemSection-matched design system
 *
 * Design parity with EcosystemSection.jsx:
 *  • GSAP + ScrollTrigger (replaces IntersectionObserver)
 *  • fp-wrap / fp-bg-grid / fp-inner structure (mirrors eco-wrap / eco-bg-grid / eco-inner)
 *  • FpCard component with identical GSAP hover + scroll reveal as EcoCard
 *  • fp-eyebrow + fp-dot + fp-h2 + fp-sub typography chain
 *  • eco-phone-frame style carousel (eco-phone-* classes)
 *  • All corners sharp (border-radius 0) matching EcosystemSection aesthetic
 */

import { useEffect, useRef, useState, useMemo, useCallback, memo } from 'react'
import gsap from 'gsap'
import FlyhubHero from '../../components/FlyhubHero'
import './FlyhubPage.css'



const API = process.env.REACT_APP_API_URL || ''

/* ─────────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────────── */
const STEPS = [
    {
        num: '01', tag: 'STEP ONE',
        title: 'Create Your Profile',
        tagline: 'Email & Google verification supported.',
        desc: 'Sign up in minutes. Verify your identity and tell us about your drone experience and needs.',
        accent: '#5d179e', rgb: '93,23,158',
    },
    {
        num: '02', tag: 'STEP TWO',
        title: 'Explore & Connect',
        tagline: 'Filter by brand, budget, location & cert level.',
        desc: 'Browse drones, services, jobs, and rentals. Smart filters help you find exactly what you need.',
        accent: '#e85d26', rgb: '232,93,38',
    },
    {
        num: '03', tag: 'STEP THREE',
        title: 'Complete Securely',
        tagline: 'Bank-grade encryption. 30-day returns.',
        desc: 'Protected payments, verified delivery, and escrow protection on every transaction.',
        accent: '#22c55e', rgb: '34,197,94',
    },
]

const FEATURES = [
    {
        icon: '🛒', tag: 'BUY & SELL',
        title: 'Marketplace',
        tagline: 'New and used drones, verified.',
        desc: 'Buy and sell new and used drones, parts, and accessories from verified sellers across India.',
        accent: '#e85d26', rgb: '232,93,38',
        urlLabel: 'Browse listings',
    },
    {
        icon: '🔧', tag: 'SHORT-TERM',
        title: 'Rentals',
        tagline: 'Test before you invest.',
        desc: 'Short-term drone and equipment rentals. Test before you invest — delivered to your location.',
        accent: '#0ea5e9', rgb: '14,165,233',
        urlLabel: 'Rent a drone',
    },
    {
        icon: '📷', tag: 'HIRE PILOTS',
        title: 'Services',
        tagline: 'Certified pilots, ready to fly.',
        desc: 'Connect with certified pilots for aerial photography, surveying, and infrastructure work.',
        accent: '#5d179e', rgb: '93,23,158',
        urlLabel: 'Find a pilot',
    },
    {
        icon: '💼', tag: 'DRONE CAREERS',
        title: 'Job Portal',
        tagline: 'Your next drone role, here.',
        desc: 'Find pilot positions in agriculture, film, construction — or post opportunities for your team.',
        accent: '#22c55e', rgb: '34,197,94',
        urlLabel: 'Browse jobs',
    },
    {
        icon: '🎓', tag: 'GET CERTIFIED',
        title: 'Training',
        tagline: 'DGCA-aligned certification.',
        desc: 'DGCA-aligned courses with hands-on flight training. Go from beginner to certified pilot.',
        accent: '#f68708', rgb: '246,135,8',
        urlLabel: 'View courses',
    },
    {
        icon: '🤝', tag: 'CONNECT',
        title: 'Community',
        tagline: "India's pilot network.",
        desc: 'Forums, local meetups, and skill-sharing workshops for pilots of every experience level.',
        accent: '#e85d26', rgb: '232,93,38',
        urlLabel: 'Join now',
    },
]

const SCREENSHOTS = [
    { key: 'gallery_screen_1', label: 'Marketplace', accent: '#e85d26' },
    { key: 'gallery_screen_2', label: 'Categories', accent: '#0ea5e9' },
    { key: 'gallery_screen_3', label: 'Rentals', accent: '#22c55e' },
    { key: 'gallery_screen_4', label: 'Profiles', accent: '#5d179e' },
    { key: 'gallery_screen_5', label: 'Training', accent: '#f68708' },
]

const TRUST = [
    {
        icon: '🛡️', tag: 'PILLAR 01',
        title: 'Verified Network',
        desc: 'Every seller, pilot, and provider undergoes identity verification before listing on Flyhub.',
        accent: '#5d179e', rgb: '93,23,158',
        badges: [{ label: 'ID Verified', type: 'live' }, { label: 'Active Monitoring', type: 'info' }],
    },
    {
        icon: '🔒', tag: 'PILLAR 02',
        title: 'Secure Payments',
        desc: 'Bank-grade encryption, escrow protection, and fraud monitoring on every transaction.',
        accent: '#0ea5e9', rgb: '14,165,233',
        badges: [{ label: 'Escrow Protected', type: 'live' }, { label: 'Fraud Monitoring', type: 'info' }],
    },
    {
        icon: '🚚', tag: 'PILLAR 03',
        title: 'Buyer Protection',
        desc: '30-day returns, dedicated support, and a transparent refund policy — always.',
        accent: '#22c55e', rgb: '34,197,94',
        badges: [{ label: '30-Day Returns', type: 'live' }, { label: 'Dedicated Support', type: 'info' }],
    },
]

const INDUSTRIES = [
    { emoji: '🌾', name: 'Agriculture' },
    { emoji: '🏗️', name: 'Construction' },
    { emoji: '🎬', name: 'Filmmaking' },
    { emoji: '🏠', name: 'Real Estate' },
    { emoji: '🔍', name: 'Inspections' },
    { emoji: '🗺️', name: 'Surveying' },
    { emoji: '🚒', name: 'Emergency' },
    { emoji: '📡', name: 'Telecom' },
]

const ROLES = [
    {
        tag: 'FOR BUYERS', icon: '🛒',
        title: 'Find Your Perfect Drone',
        tagline: 'Verified listings, protected payments.',
        desc: 'Browse thousands of verified drones, parts, and accessories. Escrow-protected payments and 30-day returns on every purchase.',
        points: ['Verified listings only', 'Smart recommendations', 'Escrow-protected payments', '30-day return policy'],
        accent: '#5d179e', rgb: '93,23,158',
        url: 'https://flyhub.info/', urlLabel: 'Start Buying',
    },
    {
        tag: 'FOR SELLERS', icon: '📦',
        title: 'Reach Serious Buyers',
        tagline: 'Free listing, instant payouts.',
        desc: 'List your drones and parts for free. Reach thousands of verified buyers across India with seller analytics and badge system.',
        points: ['Free listing creation', 'Instant payment processing', 'Seller analytics dashboard', 'Verified badge system'],
        accent: '#e85d26', rgb: '232,93,38',
        url: 'https://flyhub.info/', urlLabel: 'Start Selling',
    },
    {
        tag: 'FOR PILOTS', icon: '🚁',
        title: 'Launch Your Career',
        tagline: 'Jobs, gigs, and DGCA support.',
        desc: 'Browse drone job postings and freelance opportunities. Full-time roles, gig work, and DGCA certification support all in one place.',
        points: ['Browse drone job postings', 'Freelance & full-time roles', 'DGCA certification support', 'Direct employer connections'],
        accent: '#22c55e', rgb: '34,197,94',
        url: 'https://flyhub.info/', urlLabel: 'Find Work',
    },
]

const TRUST_BADGES = [
    '🚚 Free Shipping over ₹40,000',
    '↩ 30-Day Easy Returns',
    '💳 Transparent Refunds',
    '🔐 100% Secure Payment',
]

const INTRO_PILLS = ['🛒 Marketplace', '🔧 Rentals', '💼 Jobs', '🎓 Training', '🤝 Community']

/* ─────────────────────────────────────────────────────────────────
   HEADER ANIMATION HOOK
   Mirrors EcosystemSection's gsap.context pattern for
   eyebrow → h2 → sub within a section wrapper ref
───────────────────────────────────────────────────────────────── */
function useHeaderAnim(wrapRef) {
    useEffect(() => {
        if (!wrapRef?.current) return
        const ctx = gsap.context(() => {
            const selectors = [
                { sel: '.fp-eyebrow', yFrom: 30, dur: 0.8 },
                { sel: '.fp-h2', yFrom: 60, dur: 1.1 },
                { sel: '.fp-sub', yFrom: 30, dur: 0.9 },
                { sel: '.fp-pills', yFrom: 30, dur: 0.8 },
                { sel: '.fp-brand-card', yFrom: 40, dur: 0.9 },
            ]
            selectors.forEach(({ sel, yFrom, dur }) => {
                const el = wrapRef.current.querySelector(sel)
                if (!el) return
                gsap.fromTo(el,
                    { opacity: 0, y: yFrom },
                    {
                        opacity: 1, y: 0, duration: dur, ease: 'expo.out',
                        scrollTrigger: {
                            trigger: el,
                            start: sel === '.fp-h2' ? 'top 85%' : 'top 88%',
                            toggleActions: 'play none none none',
                        },
                    }
                )
            })
        }, wrapRef)
        return () => ctx.revert()
    }, [])
}

/* ─────────────────────────────────────────────────────────────────
   FpCard — EXACT replication of EcoCard animation pattern
   • GSAP fromTo opacity/y on ScrollTrigger
   • GSAP mouse enter/leave lift+scale
   • Renders: fp-card-glow → children → fp-card-line
───────────────────────────────────────────────────────────────── */
function FpCard({ children, index = 0, accent, rgb, className = '' }) {
    const ref = useRef(null)
    const readyRef = useRef(false)

    useEffect(() => {
        const el = ref.current
        if (!el) return
        gsap.fromTo(el,
            { opacity: 0, y: 50 },
            {
                opacity: 1, y: 0, duration: 0.9, ease: 'power3.out',
                delay: index * 0.12,
                onComplete: () => { readyRef.current = true },
                scrollTrigger: {
                    trigger: el,
                    start: 'top 88%',
                    toggleActions: 'play none none none',
                },
            }
        )
    }, [index])

    const onEnter = () => {
        if (!readyRef.current) return
        gsap.to(ref.current, { y: -10, scale: 1.018, duration: 0.45, ease: 'power3.out', overwrite: 'auto' })
    }
    const onLeave = () => {
        if (!readyRef.current) return
        gsap.to(ref.current, { y: 0, scale: 1, duration: 0.55, ease: 'expo.out', overwrite: 'auto' })
    }

    return (
        <div
            ref={ref}
            className={`fp-card ${className}`}
            style={{ '--a': accent, '--rgb': rgb, opacity: 0 }}
            onMouseEnter={onEnter}
            onMouseLeave={onLeave}
        >
            <div className="fp-card-glow" />
            {children}
            <div className="fp-card-line" style={{ background: accent }} />
        </div>
    )
}

/* ─────────────────────────────────────────────────────────────────
   SCREEN CAROUSEL
   Phone frames styled as eco-phone-frame
   Performance: memoised styles, useCallback handlers, React.memo wrapper
───────────────────────────────────────────────────────────────── */
const ScreenCarousel = memo(function ScreenCarousel({ media }) {
    const [active, setActive] = useState(2)
    const total = SCREENSHOTS.length

    const prev = useCallback(() => setActive(a => Math.max(0, a - 1)), [])
    const next = useCallback(() => setActive(a => Math.min(total - 1, a + 1)), [total])

    const phoneStyles = useMemo(() =>
        SCREENSHOTS.map((_, i) => {
            const offset = i - active
            const abs = Math.abs(offset)
            const hidden = abs > 2
            return {
                zIndex: 10 - abs,
                transform: `translate3d(${offset * 200}px, 0, 0) scale(${abs === 0 ? 1 : abs === 1 ? 0.78 : 0.62}) rotateY(${offset * -12}deg)`,
                opacity: hidden ? 0 : abs === 2 ? 0.28 : abs === 1 ? 0.65 : 1,
                pointerEvents: hidden ? 'none' : 'auto',
                cursor: abs === 0 ? 'default' : 'pointer',
            }
        }),
        [active])

    return (
        <div className="fp-carousel-wrap">
            {/* 3D track */}
            <div className="fp-carousel-track">
                {SCREENSHOTS.map((s, i) => {
                    const imgUrl = media[s.key]
                    return (
                        <div
                            key={i}
                            className="fp-carousel-phone"
                            onClick={() => setActive(i)}
                            style={phoneStyles[i]}
                        >
                            {/* eco-phone-frame identical structure */}
                            <div className="fp-phone-frame" style={{ '--a': s.accent }}>
                                <div className="fp-phone-notch" />
                                <div className="fp-phone-screen">
                                    {imgUrl
                                        ? <img src={imgUrl} alt={s.label} className="fp-phone-img" loading="lazy" decoding="async" />
                                        : <div className="fp-phone-placeholder">
                                            <span className="fp-ph-key">{s.key}</span>
                                            <span className="fp-ph-hint">UPLOAD VIA MEDIA MANAGER</span>
                                        </div>
                                    }
                                    <div className="fp-phone-shine" />
                                </div>
                                <div className="fp-phone-bar" style={{ background: s.accent }} />
                            </div>
                            {/* Label only for active */}
                            {i === active && (
                                <div className="fp-phone-label" style={{ color: s.accent, borderColor: `${s.accent}44` }}>
                                    {s.label}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Dots */}
            <div className="fp-carousel-dots">
                {SCREENSHOTS.map((s, i) => (
                    <button
                        key={i}
                        className={`fp-carousel-dot${i === active ? ' fp-carousel-dot--on' : ''}`}
                        style={{ background: i === active ? s.accent : 'rgba(13,10,24,0.15)' }}
                        onClick={() => setActive(i)}
                        aria-label={s.label}
                    />
                ))}
            </div>

            {/* Arrows */}
            <div className="fp-carousel-nav">
                <button className="fp-carousel-arr" onClick={prev} disabled={active === 0} aria-label="Previous">←</button>
                <button className="fp-carousel-arr" onClick={next} disabled={active === total - 1} aria-label="Next">→</button>
            </div>
        </div>
    )
})

/* ─────────────────────────────────────────────────────────────────
   PAGE COMPONENT
───────────────────────────────────────────────────────────────── */
export default function FlyhubPage() {
    const [media, setMedia] = useState({})

    /* SEO + media fetch */
    useEffect(() => {
        document.title = "Flyhub | India's Ultimate Drone Marketplace & Pilot Hub"
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) metaDesc.setAttribute('content', "Flyhub by Aviatricks: India's all-in-one drone platform.")
        fetch(`${API}/api/media/flyhub_gallery`)
            .then(r => r.json())
            .then(d => setMedia(d))
            .catch(() => { })
    }, [])

    /* Section refs — one per section for gsap.context scoping */
    const introRef = useRef(null)
    const hiwRef = useRef(null)
    const featRef = useRef(null)
    const screensRef = useRef(null)
    const trustRef = useRef(null)
    const indRef = useRef(null)
    const rolesRef = useRef(null)

    useHeaderAnim(introRef)
    useHeaderAnim(hiwRef)
    useHeaderAnim(featRef)
    useHeaderAnim(screensRef)
    useHeaderAnim(trustRef)
    useHeaderAnim(indRef)
    useHeaderAnim(rolesRef)

    return (
        <>
            <FlyhubHero />

            {/* ══════════ 1. ABOUT ══════════ */}
            <section id="about" ref={introRef} className="fp-wrap">
                <div className="fp-bg-grid" />
                <div className="fp-inner fp-intro-inner">

                    {/* Left — text */}
                    <div className="fp-intro-text">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}>
                            <span className="fp-dot" />ABOUT FLYHUB
                        </div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>
                            India's All-in-One<br /><em>Drone Platform.</em>
                        </h2>
                        <p className="fp-sub fp-intro-sub" style={{ opacity: 0 }}>
                            Flyhub connects pilots, buyers, sellers, and businesses on a single
                            trusted platform. Whether you're buying your first drone or running
                            a professional fleet — Flyhub has everything you need.
                        </p>
                        <div className="fp-pills" style={{ opacity: 0 }}>
                            {INTRO_PILLS.map((p, i) => (
                                <a key={i} href="https://flyhub.info/" target="_blank" rel="noreferrer" className="fp-pill">
                                    {p}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Right — brand card (dark, matches eco dark card aesthetic) */}
                    <div className="fp-intro-card-wrap">
                        <a href="https://flyhub.info" target="_blank" rel="noreferrer" className="fp-brand-link">
                            <div className="fp-brand-card" style={{ opacity: 0 }}>
                                <div className="fp-brand-grid" />
                                <div className="fp-bc-sub">Aviatricks Aero Labs</div>
                                <div className="fp-bc-name">FLYHUB</div>
                                <div className="fp-bc-tag">India's Drone Ecosystem</div>
                                <div className="fp-bc-status">
                                    <span className="fp-status-dot" />
                                    LAUNCHING SOON
                                </div>
                            </div>
                        </a>
                    </div>

                </div>
            </section>

            {/* ══════════ 2. HOW IT WORKS ══════════ */}
            <section id="how-it-works" ref={hiwRef} className="fp-wrap fp-wrap--white">
                <div className="fp-bg-grid" />
                <div className="fp-inner">
                    <div className="fp-header">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />SIMPLE PROCESS</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>Get Started in<br /><em>3 Steps.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>From signup to your first transaction — Flyhub is designed to be fast, safe, and intuitive.</p>
                    </div>
                    <div className="fp-steps-grid">
                        {STEPS.map((s, i) => (
                            <FpCard key={i} index={i} accent={s.accent} rgb={s.rgb}>
                                <div className="fp-card-top">
                                    <span className="fp-card-tag" style={{ color: s.accent, borderColor: `rgba(${s.rgb},0.3)` }}>{s.tag}</span>
                                    <span className="fp-step-num" style={{ color: s.accent }}>{s.num}</span>
                                </div>
                                <div className="fp-card-info">
                                    <h3 className="fp-card-name">{s.title}</h3>
                                    <p className="fp-card-tagline" style={{ color: s.accent }}>{s.tagline}</p>
                                    <p className="fp-card-desc">{s.desc}</p>
                                </div>
                            </FpCard>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════ 3. FEATURES ══════════ */}
            <section id="features" ref={featRef} className="fp-wrap">
                <div className="fp-bg-grid" />
                <div className="fp-inner">
                    <div className="fp-header">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />EVERYTHING IN ONE APP</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>The Ultimate<br /><em>Drone Ecosystem.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>Flyhub consolidates the entire drone industry into one powerful platform — from consumer hobbyists to enterprise operators.</p>
                    </div>
                    <div className="fp-feat-grid">
                        {FEATURES.map((f, i) => (
                            <FpCard key={i} index={i} accent={f.accent} rgb={f.rgb}>
                                <div className="fp-card-top">
                                    <span className="fp-card-tag" style={{ color: f.accent, borderColor: `rgba(${f.rgb},0.3)` }}>{f.tag}</span>
                                    <span className="fp-feat-icon">{f.icon}</span>
                                </div>
                                <div className="fp-card-info">
                                    <h3 className="fp-card-name">{f.title}</h3>
                                    <p className="fp-card-tagline" style={{ color: f.accent }}>{f.tagline}</p>
                                    <p className="fp-card-desc">{f.desc}</p>
                                    <a href="https://flyhub.info/" target="_blank" rel="noreferrer" className="fp-card-link" style={{ color: f.accent }}>
                                        <span>{f.urlLabel}</span>
                                        <span className="fp-link-arrow">→</span>
                                    </a>
                                </div>
                            </FpCard>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════ 4. APP SCREENSHOTS ══════════ */}
            <section id="app" ref={screensRef} className="fp-wrap fp-wrap--white">
                <div className="fp-bg-grid" />
                <div className="fp-inner">
                    <div className="fp-header">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />INSIDE THE APP</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>Designed for<br /><em>Every Pilot.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>A clean, powerful interface built for the way drone professionals actually work.</p>
                    </div>
                </div>

                <ScreenCarousel media={media} />

                <div className="fp-inner fp-store-row">
                    <p className="fp-store-lbl">COMING SOON ON</p>
                    <a href="https://flyhub.info" target="_blank" rel="noreferrer" className="fp-store-btn">
                        <span className="fp-store-icon">▶</span>
                        <span className="fp-store-text">
                            <small>GET IT ON</small>
                            <strong>GOOGLE PLAY</strong>
                        </span>
                    </a>
                </div>
            </section>

            {/* ══════════ 5. TRUST ══════════ */}
            <section id="trust" ref={trustRef} className="fp-wrap">
                <div className="fp-bg-grid" />
                <div className="fp-inner fp-trust-inner">

                    {/* Left — text + badges */}
                    <div className="fp-trust-left">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />BUILT ON TRUST</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>Your Safety is Our<br /><em>Infrastructure.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>
                            Every feature on Flyhub is designed with security, transparency, and
                            buyer protection at its core. We built the trust layer first.
                        </p>
                        <div className="fp-trust-badges">
                            {TRUST_BADGES.map((b, i) => (
                                <span key={i} className="fp-trust-badge">{b}</span>
                            ))}
                        </div>
                    </div>

                    {/* Right — trust cards (fp-card pattern) */}
                    <div className="fp-trust-cards">
                        {TRUST.map((t, i) => (
                            <FpCard key={i} index={i} accent={t.accent} rgb={t.rgb}>
                                <div className="fp-card-top">
                                    <span className="fp-card-tag" style={{ color: t.accent, borderColor: `rgba(${t.rgb},0.3)` }}>{t.tag}</span>
                                    <span className="fp-trust-icon">{t.icon}</span>
                                </div>
                                <div className="fp-card-info">
                                    <h3 className="fp-card-name fp-card-name--sm">{t.title}</h3>
                                    <p className="fp-card-desc">{t.desc}</p>
                                    <div className="fp-card-badges">
                                        {t.badges.map((b, j) => (
                                            <span key={j}
                                                className={`fp-eco-badge fp-eco-badge--${b.type}`}
                                                style={b.type === 'live' ? { borderColor: t.accent, color: t.accent } : {}}>
                                                {b.type === 'live' && <span className="fp-badge-pulse" style={{ background: t.accent }} />}
                                                {b.label}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </FpCard>
                        ))}
                    </div>

                </div>
            </section>

            {/* ══════════ 6. INDUSTRIES ══════════ */}
            <section id="industries" ref={indRef} className="fp-wrap fp-wrap--white">
                <div className="fp-bg-grid" />
                <div className="fp-inner fp-ind-inner">

                    {/* Left — text */}
                    <div className="fp-ind-text">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />CROSS-INDUSTRY</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>Serving Every<br /><em>Industry.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>
                            Agriculture, real estate, filmmaking, construction, inspections, surveying —
                            Flyhub is built for every sector that flies professionally.
                        </p>
                        <a href="https://flyhub.info/" target="_blank" rel="noreferrer" className="fp-cta-link" style={{ color: '#5d179e' }}>
                            <span>Explore All Industries</span>
                            <span className="fp-link-arrow">→</span>
                        </a>
                    </div>

                    {/* Right — industry chips */}
                    <div className="fp-ind-grid">
                        {INDUSTRIES.map((ind, i) => (
                            <div key={i} className="fp-ind-chip">
                                <span className="fp-ind-emoji">{ind.emoji}</span>
                                <span className="fp-ind-name">{ind.name}</span>
                            </div>
                        ))}
                    </div>

                </div>
            </section>

            {/* ══════════ 7. ROLES ══════════ */}
            <section id="roles" ref={rolesRef} className="fp-wrap">
                <div className="fp-bg-grid" />
                <div className="fp-inner">
                    <div className="fp-header">
                        <div className="fp-eyebrow" style={{ opacity: 0 }}><span className="fp-dot" />FIND YOUR PLACE</div>
                        <h2 className="fp-h2" style={{ opacity: 0 }}>Choose<br /><em>Your Role.</em></h2>
                        <p className="fp-sub" style={{ opacity: 0 }}>Professional platform built for every participant in India's drone industry.</p>
                    </div>
                    <div className="fp-roles-grid">
                        {ROLES.map((r, i) => (
                            <FpCard key={i} index={i} accent={r.accent} rgb={r.rgb}>
                                <div className="fp-card-top">
                                    <span className="fp-card-tag" style={{ color: r.accent, borderColor: `rgba(${r.rgb},0.3)` }}>{r.tag}</span>
                                    <span className="fp-feat-icon">{r.icon}</span>
                                </div>
                                <div className="fp-card-info">
                                    <h3 className="fp-card-name">{r.title}</h3>
                                    <p className="fp-card-tagline" style={{ color: r.accent }}>{r.tagline}</p>
                                    <p className="fp-card-desc">{r.desc}</p>
                                    <ul className="fp-role-list">
                                        {r.points.map((p, j) => (
                                            <li key={j} className="fp-role-item" style={{ '--a': r.accent }}>{p}</li>
                                        ))}
                                    </ul>
                                    <a href={r.url} target="_blank" rel="noreferrer" className="fp-card-link" style={{ color: r.accent }}>
                                        <span>{r.urlLabel}</span>
                                        <span className="fp-link-arrow">→</span>
                                    </a>
                                </div>
                            </FpCard>
                        ))}
                    </div>
                </div>
            </section>
        </>
    )
}